import { useEffect, useRef, useCallback, useState } from "react";
import { useStore } from "@/store/useStore";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getWsUrl } from "@/config/api";

export function useNotifications() {
  const { user, token } = useStore();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const [connected, setConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);

  const connect = useCallback(() => {
    if (!user?.id || !token) return;

    // Get WebSocket URL from backend URL
    const wsUrl = `${getWsUrl()}/api/ws/notifications/${user.id}?token=${token}`;

    try {
      wsRef.current = new WebSocket(wsUrl);

      wsRef.current.onopen = () => {
        console.log("Notifications WebSocket connected");
        setConnected(true);
        // Clear any reconnection timeout
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }
      };

      wsRef.current.onmessage = (event) => {
        if (event.data === "pong") return;
        try {
          const data = JSON.parse(event.data);
          handleNotification(data);
        } catch (err) {
          console.error("Failed to parse notification:", err);
        }
      };

      wsRef.current.onclose = () => {
        console.log("Notifications WebSocket closed");
        setConnected(false);
        // Attempt to reconnect after 5 seconds
        reconnectTimeoutRef.current = setTimeout(() => {
          connect();
        }, 5000);
      };

      wsRef.current.onerror = (error) => {
        console.error("Notifications WebSocket error:", error);
      };

      // Ping every 30 seconds to keep connection alive
      const pingInterval = setInterval(() => {
        if (wsRef.current?.readyState === WebSocket.OPEN) {
          wsRef.current.send("ping");
        }
      }, 30000);

      return () => {
        clearInterval(pingInterval);
      };
    } catch (err) {
      console.error("Failed to connect WebSocket:", err);
    }
  }, [user?.id, token]);

  const handleNotification = useCallback((data) => {
    // Add to notifications list
    setNotifications(prev => [data, ...prev].slice(0, 50));

    // Show toast based on notification type
    switch (data.type) {
      case "friend_request":
        toast.info(`👥 ${data.message}`, {
          action: {
            label: t("common.view"),
            onClick: () => navigate("/friends")
          },
          duration: 10000
        });
        break;

      case "friend_accepted":
        toast.success(`✅ ${data.message}`, {
          duration: 5000
        });
        break;

      case "challenge":
        toast(`⚔️ ${data.message}`, {
          action: {
            label: t("friends.accept"),
            onClick: () => navigate("/friends")
          },
          duration: 30000,
          className: "challenge-toast"
        });
        // Play sound effect
        playNotificationSound();
        break;

      case "challenge_accepted":
        toast.success(`🎮 ${data.message}`, {
          action: {
            label: t("common.play"),
            onClick: () => navigate(`/game/${data.game_id}`)
          },
          duration: 10000
        });
        // Auto-navigate to game
        setTimeout(() => {
          navigate(`/game/${data.game_id}`);
        }, 2000);
        break;

      case "kyc_status":
        if (data.status === "approved") {
          toast.success(`✅ ${t("notifications.kycApproved")}`, {
            duration: 10000
          });
        } else if (data.status === "rejected") {
          toast.error(`❌ ${t("notifications.kycRejected")}`, {
            duration: 10000
          });
        } else {
          toast.info(`📋 ${data.message}`);
        }
        break;

      case "pong":
        // Heartbeat response, ignore
        break;

      default:
        console.log("Unknown notification type:", data.type);
    }
  }, [navigate, t]);

  const playNotificationSound = () => {
    try {
      // Simple beep using Web Audio API
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = "sine";
      gainNode.gain.value = 0.1;
      
      oscillator.start();
      oscillator.stop(audioContext.currentTime + 0.2);
    } catch (err) {
      // Ignore audio errors
    }
  };

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    setConnected(false);
  }, []);

  useEffect(() => {
    connect();
    return () => disconnect();
  }, [connect, disconnect]);

  const clearNotifications = useCallback(() => {
    setNotifications([]);
  }, []);

  return {
    connected,
    notifications,
    clearNotifications
  };
}

export default useNotifications;
